/**
 * Адреса всех бэкендов кабинета: хост, префикс пути и ручки каждого сервиса.
 * Правится здесь — по коду адреса не разбросаны.
 *
 * Приоритет базового адреса, сверху вниз:
 *   1. рантайм-конфиг `/config.js` — контейнер генерирует его из переменных окружения;
 *   2. переменная сборки VITE_*  — если адрес вшивается в образ;
 *   3. host + basePath из блока сервиса ниже — значения по умолчанию для локальной работы.
 *
 * Новый сервис — новый блок ниже, строка в .env.example и в deploy/docker-entrypoint.sh.
 */
import { readRuntimeValue, type RuntimeConfig } from './runtimeConfig';

/** Авторизация: вход по телефону и SMS-коду, выдача и отзыв токенов. */
export const authApi = defineService({
  title: 'сервис авторизации',
  /** Схема и хост. Пустая строка — same-origin (нужно проксирование, см. deploy/nginx.conf). */
  host: 'http://127.0.0.1:60021',
  /** Префикс пути этого сервиса. */
  basePath: '/api/v1.0',
  /** Ключ в рантайм-конфиге и переменная сборки, перекрывающие host + basePath. */
  runtimeKey: 'AUTH_API_BASE_URL',
  envVar: 'VITE_AUTH_API_BASE_URL',
  envValue: import.meta.env.VITE_AUTH_API_BASE_URL,
  endpoints: {
    /** POST — запросить SMS-код на номер телефона. */
    requestSmsCode: '/attempts',
    /** POST — подтвердить код и получить пару токенов. */
    confirmSmsCode: (attemptId: string) => `/attempts/${encodeURIComponent(attemptId)}`,
    /** POST — обменять refresh на новую пару. Сервер ротирует refresh. */
    refreshTokens: '/tokens/refresh',
    /** POST — отозвать refresh-токен. */
    logout: '/tokens/logout',
  },
});

/** Админка: данные кабинета. */
export const adminApi = defineService({
  title: 'API админки',
  host: 'http://127.0.0.1:60031',
  basePath: '/api/v1.0',
  runtimeKey: 'ADMIN_API_BASE_URL',
  envVar: 'VITE_ADMIN_API_BASE_URL',
  envValue: import.meta.env.VITE_ADMIN_API_BASE_URL,
  endpoints: {
    /** GET — личность вошедшего, роль и первые страницы разделов. */
    init: '/init',
    /** GET — страница списка пользователей. */
    users: '/users',
    /**
     * POST — изменение баланса пользователя: `{ amount, user_id }`.
     * Допустимы только суммы из `BALANCE_CHANGE_AMOUNTS`, ответ 204.
     */
    transactions: '/transactions',
  },
});

/**
 * Centrifugo: сокет для живых обновлений. Подключение поднимается после входа
 * и служит признаком «онлайн» на панели.
 *
 * Адрес задаётся так же, как у сервисов: рантайм-конфиг REALTIME_URL, затем
 * переменная сборки, затем значение по умолчанию для локальной работы.
 */
const REALTIME_DEFAULT_URL = 'ws://127.0.0.1:60051/connection/websocket';

export function getRealtimeUrl(): string {
  return (
    readRuntimeValue('REALTIME_URL') ??
    import.meta.env.VITE_REALTIME_URL?.trim() ??
    REALTIME_DEFAULT_URL
  );
}

/** Сервис пользователей: изменение собственных данных. */
export const usersServiceApi = defineService({
  title: 'сервис пользователей',
  host: 'http://127.0.0.1:60041',
  basePath: '/api/v1.0',
  runtimeKey: 'USERS_API_BASE_URL',
  envVar: 'VITE_USERS_API_BASE_URL',
  envValue: import.meta.env.VITE_USERS_API_BASE_URL,
  endpoints: {
    /** POST — сохранить своё имя и фамилию. Ответ 204. */
    updateProfile: '/users',
  },
});

/** Все сервисы одним объектом — для перебора и диагностики. */
export const API = {
  auth: authApi,
  admin: adminApi,
  users: usersServiceApi,
} as const;

export type ApiServiceName = keyof typeof API;

/* ===========================================================================
   Механика. Ниже менять нечего.
   =========================================================================== */

interface ServiceDefinition<E> {
  title: string;
  host: string;
  basePath: string;
  runtimeKey: keyof RuntimeConfig;
  envVar: string;
  envValue: string | undefined;
  endpoints: E;
}

export interface ApiService<E> {
  title: string;
  envVar: string;
  /** Откуда взят адрес — видно в диагностике. */
  source: 'runtime' | 'build' | 'default';
  /** Итоговый базовый адрес без завершающего слэша. */
  baseUrl: string;
  endpoints: E;
  /** Полный адрес ручки: service.url(service.endpoints.init). */
  url: (path: string) => string;
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function resolveBaseUrl(definition: ServiceDefinition<unknown>): {
  baseUrl: string;
  source: ApiService<unknown>['source'];
} {
  const runtimeValue = readRuntimeValue(definition.runtimeKey);
  if (runtimeValue) {
    return { baseUrl: trimTrailingSlash(runtimeValue), source: 'runtime' };
  }

  const buildValue = definition.envValue?.trim();
  if (buildValue) {
    return { baseUrl: trimTrailingSlash(buildValue), source: 'build' };
  }

  return {
    baseUrl: trimTrailingSlash(`${trimTrailingSlash(definition.host)}${definition.basePath}`),
    source: 'default',
  };
}

function defineService<E>(definition: ServiceDefinition<E>): ApiService<E> {
  const { baseUrl, source } = resolveBaseUrl(definition);

  return {
    title: definition.title,
    envVar: definition.envVar,
    source,
    baseUrl,
    endpoints: definition.endpoints,
    url: (path: string) => `${baseUrl}${path}`,
  };
}
